// Identity key derivation: BIP-39 seed → HKDF-SHA256 → P-256 scalar →
// ECDSA P-256 keypair.
//
// This module implements the deterministic identity-derivation pipeline
// described in `design.md` ("Crypto_Layer / packages/crypto") and called
// out by Requirements 1.2, 1.3, and 3.3:
//
//   1. Run HKDF-SHA256 over the BIP-39 64-byte seed using:
//        salt = SHA-256("link.identity.v1.salt")
//        info = "link.identity.v1.ecdsa-p256"
//        L    = 48 bytes
//
//   2. Interpret the 48-byte HKDF output as a big-endian integer modulo
//      the P-256 curve order `n`, rejecting candidates that map to 0
//      (mod n) or that are >= n. Rejection sampling on a 48-byte
//      candidate gives a bias well below 2^-128 — safe for a single
//      identity key.
//
//   3. On rejection, append a single counter byte to `info` (starting
//      at 0x01 and incrementing on each retry) and re-expand HKDF.
//      An attacker who could push us past 256 retries would need to
//      have broken HMAC-SHA256; we treat counter overflow as a bug.
//
//   4. Import the resulting 32-byte big-endian scalar as a P-256
//      private key, derive its public key, then re-import the private
//      key as a non-extractable `CryptoKey` so callers can hand the
//      private key to `Key_Store` without ever round-tripping the
//      scalar through application memory again.
//
// The 48-byte HKDF length matches FIPS 186-4 §B.4.1 ("Key Pair
// Generation Using Extra Random Bits"), which RFC 6979 and the IRTF
// `hash_to_field` construction also use for P-256 to bound the modular
// bias. We keep the rejection step anyway because the design document
// is explicit about it and the property test in 3.4 verifies the scalar
// lies in `[1, n-1]`.
//
// _Requirements: 1.2, 1.3, 3.3_

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Plain-ASCII tag used to derive the HKDF salt. */
const SALT_INPUT = "link.identity.v1.salt";

/** HKDF `info` parameter for the first attempt (no counter byte). */
const INFO_BASE = "link.identity.v1.ecdsa-p256";

/**
 * Length of the HKDF output in bytes. 48 bytes (= 384 bits) gives at
 * least 128 bits of headroom above the 256-bit curve order, which keeps
 * the modular bias on the rejection-sampled scalar negligible.
 */
const HKDF_OUTPUT_BYTES = 48;

/** Length of the BIP-39 seed expected by `deriveIdentityKey`. */
const BIP39_SEED_BYTES = 64;

/**
 * P-256 curve order `n` (a.k.a. `secp256r1` group order). Sourced from
 * NIST FIPS 186-4 §D.1.2.3 and SEC 2 §2.7.2:
 *
 *   n = FFFFFFFF 00000000 FFFFFFFF FFFFFFFF
 *       BCE6FAAD A7179E84 F3B9CAC2 FC632551
 */
const P256_ORDER: bigint =
  0xffffffff00000000ffffffffffffffffbce6faada7179e84f3b9cac2fc632551n;

/**
 * Maximum number of HKDF re-expansions before we give up. Reaching this
 * cap is statistically impossible for HKDF-SHA256 output (the per-try
 * rejection probability is ≈ 2^-128); we cap at 256 retries to bound
 * the counter byte to a single octet and to surface implementation
 * bugs early instead of looping forever.
 */
const MAX_REJECTION_RETRIES = 256;

/** SEC1 uncompressed-point tag. The first byte of `publicKeyRaw`. */
const SEC1_UNCOMPRESSED_TAG = 0x04;

/** Length in bytes of the uncompressed SEC1 P-256 public key. */
const SEC1_UNCOMPRESSED_LENGTH = 65;

/** Length in bytes of a P-256 scalar / coordinate. */
const P256_FIELD_BYTES = 32;

/** P-256 field prime and standard generator (SEC 2 §2.7.2). */
const P256_FIELD_PRIME =
  0xffffffff00000001000000000000000000000000ffffffffffffffffffffffffn;
const P256_GENERATOR_X =
  0x6b17d1f2e12c4247f8bce6e563a440f277037d812deb33a0f4a13945d898c296n;
const P256_GENERATOR_Y =
  0x4fe342e2fe1a7f9b8ee7eb4a7c0f9e162bce33576b315ececbb6406837bf51f5n;

const TEXT_ENCODER = new TextEncoder();

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * The output of `deriveIdentityKey`. The `privateKey` is non-extractable
 * — once handed off to the `Key_Store`, the scalar cannot be read back
 * out of Web Crypto. The `publicKeyHash` is the canonical
 * `account_id_hash` used as a primary key in the `accounts` table.
 */
export interface IdentityKeyMaterial {
  /** ECDSA P-256 private key, imported with `extractable: false`. */
  privateKey: CryptoKey;
  /** Same P-256 scalar imported for ECDH so E2E envelopes can be opened. */
  ecdhPrivateKey: CryptoKey;
  /** ECDSA P-256 public key (extractable). */
  publicKey: CryptoKey;
  /** 65-byte uncompressed SEC1 encoding of the public key. */
  publicKeyRaw: Uint8Array;
  /** SHA-256(publicKeyRaw); 32 bytes. Used as the canonical account ID hash. */
  publicKeyHash: Uint8Array;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Derive the user's long-lived ECDSA P-256 identity key from a BIP-39
 * 64-byte seed, deterministically.
 *
 * Two invocations with the same seed always produce byte-identical
 * `publicKeyRaw` and `publicKeyHash`. The returned `privateKey` is
 * non-extractable; the underlying scalar is zeroed from the local
 * buffer before the function returns.
 *
 * @throws if `seed` is not exactly {@link BIP39_SEED_BYTES} bytes long.
 * @throws if rejection sampling has not produced a valid scalar after
 *   {@link MAX_REJECTION_RETRIES} retries (statistically impossible for
 *   a working HMAC-SHA256 implementation).
 *
 * _Requirements: 1.2, 1.3, 3.3_
 */
export async function deriveIdentityKey(
  seed: Uint8Array,
): Promise<IdentityKeyMaterial> {
  if (seed.length !== BIP39_SEED_BYTES) {
    throw new Error(
      `deriveIdentityKey: expected a ${BIP39_SEED_BYTES}-byte BIP-39 seed, got ${seed.length}`,
    );
  }

  const subtle = getSubtle();

  // Step 1: derive the fixed HKDF salt = SHA-256(SALT_INPUT).
  const salt = new Uint8Array(
    await subtle.digest("SHA-256", TEXT_ENCODER.encode(SALT_INPUT)),
  );

  // Step 2: import the seed as HKDF input keying material.
  const ikm = await subtle.importKey(
    "raw",
    // Copy into a fresh buffer so the caller's seed isn't aliased into
    // Web Crypto's internal state.
    seed.slice(),
    { name: "HKDF" },
    false,
    ["deriveBits"],
  );

  const baseInfo = TEXT_ENCODER.encode(INFO_BASE);

  // Step 3: rejection sampling against P-256 order n.
  let scalar: Uint8Array | null = null;
  for (let counter = 0; counter <= MAX_REJECTION_RETRIES; counter++) {
    const info = counter === 0 ? baseInfo : appendCounter(baseInfo, counter);
    const candidate = new Uint8Array(
      await subtle.deriveBits(
        { name: "HKDF", hash: "SHA-256", salt, info },
        ikm,
        HKDF_OUTPUT_BYTES * 8,
      ),
    );

    const reduced = reduceToP256Scalar(candidate);
    // Wipe the 48-byte candidate immediately; only the reduced scalar
    // (or `null`) survives to the next iteration.
    candidate.fill(0);

    if (reduced !== null) {
      scalar = reduced;
      break;
    }
  }

  if (scalar === null) {
    // Reaching this branch means HKDF-SHA256 produced 257 consecutive
    // outputs that all fell into the [n, 2^384) gap — probability on
    // the order of 2^-32768. Treat as a bug in HMAC-SHA256, not user
    // input.
    throw new Error(
      `deriveIdentityKey: rejection sampling did not converge in ${MAX_REJECTION_RETRIES + 1} attempts`,
    );
  }

  // Step 4: derive the public point and import a complete EC JWK. Chromium
  // accepts a scalar-only PKCS#8 key and computes its missing public point,
  // but WebKit/Safari rejects that encoding with a generic DataError. Full
  // `x`, `y`, and `d` coordinates are the interoperable Web Crypto shape.
  const scalarValue = bytesToBigint(scalar);
  const publicPoint = multiplyP256Generator(scalarValue);
  const xBytes = bigintToBe32(publicPoint.x);
  const yBytes = bigintToBe32(publicPoint.y);
  const x = bytesToBase64Url(xBytes);
  const y = bytesToBase64Url(yBytes);
  const d = bytesToBase64Url(scalar);

  // Public key is always extractable so callers can hash / encode it.
  const publicKey = await subtle.importKey(
    "jwk",
    { kty: "EC", crv: "P-256", x, y, ext: true, key_ops: ["verify"] },
    { name: "ECDSA", namedCurve: "P-256" },
    true,
    ["verify"],
  );

  const privateKey = await subtle.importKey(
    "jwk",
    { kty: "EC", crv: "P-256", x, y, d, ext: false, key_ops: ["sign"] },
    { name: "ECDSA", namedCurve: "P-256" },
    false,
    ["sign"],
  );

  const ecdhPrivateKey = await subtle.importKey(
    "jwk",
    { kty: "EC", crv: "P-256", x, y, d, ext: false, key_ops: ["deriveBits"] },
    { name: "ECDH", namedCurve: "P-256" },
    false,
    ["deriveBits"],
  );

  // Web Crypto has copied the private material. Wipe the mutable scalar now;
  // the temporary base64url string is immutable and falls out of scope here.
  scalar.fill(0);

  const publicKeyRaw = new Uint8Array(SEC1_UNCOMPRESSED_LENGTH);
  publicKeyRaw[0] = SEC1_UNCOMPRESSED_TAG;
  publicKeyRaw.set(xBytes, 1);
  publicKeyRaw.set(yBytes, 1 + P256_FIELD_BYTES);
  if (
    publicKeyRaw.length !== SEC1_UNCOMPRESSED_LENGTH ||
    publicKeyRaw[0] !== SEC1_UNCOMPRESSED_TAG
  ) {
    throw new Error(
      `deriveIdentityKey: unexpected raw public key encoding (length=${publicKeyRaw.length}, tag=0x${publicKeyRaw[0]?.toString(16) ?? "??"})`,
    );
  }

  const publicKeyHash = new Uint8Array(
    await subtle.digest("SHA-256", publicKeyRaw),
  );

  return { privateKey, ecdhPrivateKey, publicKey, publicKeyRaw, publicKeyHash };
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function getSubtle(): SubtleCrypto {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    throw new Error(
      "Web Crypto subtle is not available in this runtime. " +
        "deriveIdentityKey requires a Web Crypto-capable environment " +
        "(modern browsers, Node 20+, or Bun).",
    );
  }
  return subtle;
}

/**
 * Build the HKDF `info` parameter for retry attempt `counter` (>= 1).
 * The base `info` is followed by a single big-endian counter byte.
 *
 * @internal exported for the property test in task 3.4 only.
 */
function appendCounter(baseInfo: Uint8Array, counter: number): Uint8Array {
  if (counter < 1 || counter > 255) {
    throw new Error(
      `appendCounter: counter must be in [1, 255], got ${counter}`,
    );
  }
  const out = new Uint8Array(baseInfo.length + 1);
  out.set(baseInfo, 0);
  out[baseInfo.length] = counter & 0xff;
  return out;
}

/**
 * Reduce a 48-byte big-endian HKDF output to a 32-byte big-endian
 * P-256 scalar in `[1, n-1]`, or return `null` if the candidate is
 * `0 (mod n)` or `>= n` and must be rejected.
 *
 * Returning `null` for `>= n` (rather than reducing mod `n`) is what
 * the design calls "rejection sampling"; the alternative — taking the
 * candidate modulo `n` — would skew the distribution of the resulting
 * scalar away from uniform for candidates in `[n, 2^256)`.
 *
 * Rejecting `0 (mod n)` enforces the FIPS 186-4 §6.2.1 requirement
 * that the private key lie in `[1, n-1]`.
 */
function reduceToP256Scalar(candidate: Uint8Array): Uint8Array | null {
  if (candidate.length !== HKDF_OUTPUT_BYTES) {
    throw new Error(
      `reduceToP256Scalar: expected ${HKDF_OUTPUT_BYTES}-byte input, got ${candidate.length}`,
    );
  }
  // Convert the 48-byte big-endian buffer to a non-negative bigint.
  let value = 0n;
  for (let i = 0; i < candidate.length; i++) {
    value = (value << 8n) | BigInt(candidate[i]!);
  }

  // The candidate lives in [0, 2^384). P-256 `n` is just under 2^256,
  // so a single 48-byte HKDF output reduced `mod n` is uniformly
  // distributed across [0, n) with bias ≤ 2^-128 (RFC 9380 §5.1's
  // "hash_to_field" construction). After reduction the residue cannot
  // be ≥ n by definition; the explicit `>= P256_ORDER` check is a
  // contract assertion against a broken bigint implementation.
  //
  // Reject only when the residue is 0 (FIPS 186-4 §6.2.1 requires the
  // private scalar to lie in [1, n-1]). The retry-with-counter loop
  // exists for that vanishingly rare case.
  const residue = value % P256_ORDER;
  if (residue === 0n) return null;
  if (residue >= P256_ORDER) return null; // unreachable given `% P256_ORDER`

  return bigintToBe32(residue);
}

/**
 * Encode a non-negative bigint `< 2^256` as a 32-byte big-endian buffer,
 * left-padded with zeros.
 */
function bigintToBe32(value: bigint): Uint8Array {
  if (value < 0n) {
    throw new Error("bigintToBe32: negative bigint");
  }
  const out = new Uint8Array(P256_FIELD_BYTES);
  let v = value;
  for (let i = P256_FIELD_BYTES - 1; i >= 0; i--) {
    out[i] = Number(v & 0xffn);
    v >>= 8n;
  }
  if (v !== 0n) {
    throw new Error("bigintToBe32: input does not fit in 32 bytes");
  }
  return out;
}

interface JacobianPoint {
  x: bigint;
  y: bigint;
  z: bigint;
}

function field(value: bigint): bigint {
  const reduced = value % P256_FIELD_PRIME;
  return reduced >= 0n ? reduced : reduced + P256_FIELD_PRIME;
}

function bytesToBigint(bytes: Uint8Array): bigint {
  let value = 0n;
  for (const byte of bytes) value = (value << 8n) | BigInt(byte);
  return value;
}

/** Jacobian doubling specialized for P-256's `a = -3`. */
function doublePoint(point: JacobianPoint): JacobianPoint {
  if (point.z === 0n || point.y === 0n) return { x: 0n, y: 1n, z: 0n };
  const delta = field(point.z * point.z);
  const gamma = field(point.y * point.y);
  const beta = field(point.x * gamma);
  const alpha = field(3n * field(point.x - delta) * field(point.x + delta));
  const x = field(alpha * alpha - 8n * beta);
  const z = field(field(point.y + point.z) ** 2n - gamma - delta);
  const y = field(alpha * field(4n * beta - x) - 8n * gamma * gamma);
  return { x, y, z };
}

/** Mixed Jacobian + affine addition, used to add the fixed generator. */
function addGenerator(point: JacobianPoint): JacobianPoint {
  if (point.z === 0n) {
    return { x: P256_GENERATOR_X, y: P256_GENERATOR_Y, z: 1n };
  }
  const zSquared = field(point.z * point.z);
  const generatorX = field(P256_GENERATOR_X * zSquared);
  const generatorY = field(P256_GENERATOR_Y * point.z * zSquared);
  const h = field(generatorX - point.x);
  const rHalf = field(generatorY - point.y);
  if (h === 0n) {
    return rHalf === 0n ? doublePoint(point) : { x: 0n, y: 1n, z: 0n };
  }
  const hh = field(h * h);
  const i = field(4n * hh);
  const j = field(h * i);
  const r = field(2n * rHalf);
  const v = field(point.x * i);
  const x = field(r * r - j - 2n * v);
  const y = field(r * field(v - x) - 2n * point.y * j);
  const z = field(field(point.z + h) ** 2n - zSquared - hh);
  return { x, y, z };
}

function modularPower(base: bigint, exponent: bigint): bigint {
  let result = 1n;
  let factor = field(base);
  let remaining = exponent;
  while (remaining > 0n) {
    if ((remaining & 1n) === 1n) result = field(result * factor);
    factor = field(factor * factor);
    remaining >>= 1n;
  }
  return result;
}

/** Compute `scalar · G` with one field inversion at the affine boundary. */
function multiplyP256Generator(scalar: bigint): { x: bigint; y: bigint } {
  if (scalar <= 0n || scalar >= P256_ORDER) {
    throw new Error("deriveIdentityKey: P-256 scalar is out of range");
  }
  let result: JacobianPoint = { x: 0n, y: 1n, z: 0n };
  for (let bit = 255; bit >= 0; bit -= 1) {
    result = doublePoint(result);
    if (((scalar >> BigInt(bit)) & 1n) === 1n) result = addGenerator(result);
  }
  if (result.z === 0n) {
    throw new Error("deriveIdentityKey: scalar multiplication returned infinity");
  }
  const inverseZ = modularPower(result.z, P256_FIELD_PRIME - 2n);
  const inverseZSquared = field(inverseZ * inverseZ);
  return {
    x: field(result.x * inverseZSquared),
    y: field(result.y * inverseZSquared * inverseZ),
  };
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}
